// TEAM-001 custom permissions (owner, 2026-11-04): from the invite popup, the owner picks what a
// person can do, area by area (View / Manage, with Advanced for single permissions), and saves it as
// a named custom role (POST /orgs/{org}/roles). The new role is then chosen in the invite. The server
// still checks every permission and refuses more access than the owner's own role (assertGrantable).
//   View    the area's reading permissions (orders.read, products.read, …)
//   Manage  every other everyday permission in the area; it turns View on too
//   Advanced the single permissions; sensitive ones (refunds, exports, deleting, keys) only here
import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { i18n, t } from "@ady/i18n";
import { Badge, Banner, Button, Checkbox, cx, Field, Icon, Input, Popup } from "@ady/ui";
import { api, ApiError } from "../api/client";
import { errorMessage } from "../api/errors";
import { keys } from "../api/queries";
import type { Role } from "../api/types";
import { isStepUpCancelled, stepUpErrorMessage, useStepUp } from "../auth/StepUp";
import { PopupHead } from "../finance/MoneyPopups";
import { Chip } from "../products/AddProductDialog";

/** Areas in the order a shop owner thinks of them; unknown groups follow at the end. */
const AREAS = [
  "orders",
  "catalog",
  "inventory",
  "customers",
  "pos",
  "shipping",
  "payments",
  "finance",
  "reports",
  "marketing",
  "content",
  "theme",
  "media",
  "store",
  "settings",
  "risk",
  "integrations",
  "ai",
  "team",
  "billing",
  "audit",
  "org",
];
/** The reading permissions: "View" in each area. */
const VIEW = new Set([
  "org.read",
  "team.read",
  "store.read",
  "inbox.read",
  "products.read",
  "media.read",
  "inventory.read",
  "customers.read",
  "orders.read",
  "payments.read",
  "finance.read",
  "reports.read",
  "billing.read",
  "audit.read",
]);
/** Never offered: only the business's owner closes it or hands it over. */
const HIDDEN = new Set(["org.close", "org.transfer_ownership"]);

interface Area {
  key: string;
  view: string[];
  manage: string[];
  sensitive: string[];
}

const permLabel = (id: string) =>
  i18n.has(`team.customRole.perms.${id.replaceAll(".", "_")}`)
    ? i18n.tDynamic(`team.customRole.perms.${id.replaceAll(".", "_")}`, "team.customRole.view")
    : id;
const areaText = (area: string, kind: "areas" | "areaHelp") =>
  i18n.has(`team.customRole.${kind}.${area}`)
    ? i18n.tDynamic(`team.customRole.${kind}.${area}`, "team.customRole.view")
    : kind === "areas"
      ? area
      : "";

export function CustomRoleDialog({
  open,
  onClose,
  orgId,
  onCreated,
  role,
}: {
  open: boolean;
  onClose: () => void;
  orgId: string;
  onCreated: (role: Role) => void;
  /** Edit this custom role instead of making a new one (owner, 2026-11-04). */
  role?: Role | null;
}) {
  const qc = useQueryClient();
  // Creating a role is a sensitive action (roles.manage): the server may ask "confirm it's you"
  // first; stepUp shows that check and retries (owner, 2026-11-04: the bare message showed instead).
  const stepUp = useStepUp();
  const catalogue = useQuery({
    queryKey: ["permissions"],
    queryFn: () => api.permissions(),
    enabled: open,
    staleTime: Infinity,
  });
  const [name, setName] = useState("");
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [openAreas, setOpenAreas] = useState<Set<string>>(new Set());
  const [nameError, setNameError] = useState<string>();
  const [formError, setFormError] = useState<string>();
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setName(role?.name ?? "");
    setPicked(new Set(role?.permissions ?? []));
    setOpenAreas(new Set());
    setNameError(undefined);
    setFormError(undefined);
  }, [open, role]);

  const areas = useMemo<Area[]>(() => {
    const byGroup = new Map<string, Area>();
    for (const p of catalogue.data?.permissions ?? []) {
      if (HIDDEN.has(p.id)) continue;
      const a = byGroup.get(p.group) ?? { key: p.group, view: [], manage: [], sensitive: [] };
      if (p.sensitive) a.sensitive.push(p.id);
      else if (VIEW.has(p.id)) a.view.push(p.id);
      else a.manage.push(p.id);
      byGroup.set(p.group, a);
    }
    const rank = (k: string) => (AREAS.includes(k) ? AREAS.indexOf(k) : AREAS.length);
    return [...byGroup.values()].sort((a, b) => rank(a.key) - rank(b.key));
  }, [catalogue.data]);

  const all = (ids: string[]) => ids.length > 0 && ids.every((id) => picked.has(id));
  const change = (add: string[], remove: string[] = []) =>
    setPicked((prev) => {
      const next = new Set(prev);
      for (const id of remove) next.delete(id);
      for (const id of add) next.add(id);
      return next;
    });
  const toggleView = (a: Area) =>
    all(a.view) ? change([], [...a.view, ...a.manage]) : change(a.view);
  const toggleManage = (a: Area) =>
    all(a.manage) ? change([], a.manage) : change([...a.view, ...a.manage]);
  const toggleOne = (id: string, on: boolean) => (on ? change([id]) : change([], [id]));

  const save = async () => {
    setFormError(undefined);
    const n = name.trim();
    if (!n) {
      setNameError(t("team.customRole.nameRequired"));
      return;
    }
    setNameError(undefined);
    if (picked.size === 0) {
      setFormError(t("team.customRole.noneChosen"));
      return;
    }
    setBusy(true);
    try {
      const permissions = [...picked].sort();
      const saved = await stepUp(() =>
        role
          ? api.updateRole(orgId, role.id, { name: n, permissions }, role.version)
          : api.createRole(orgId, { name: n, permissions }),
      );
      await qc.invalidateQueries({ queryKey: keys.roles(orgId) });
      onCreated(saved);
      onClose();
    } catch (err) {
      if (isStepUpCancelled(err)) return;
      const stepMsg = stepUpErrorMessage(err);
      if (err instanceof ApiError && err.code.endsWith("version_mismatch"))
        setFormError(t("team.customRole.changed"));
      else if (err instanceof ApiError && err.code === "validation.name_taken")
        setNameError(t("team.customRole.nameTaken"));
      else if (err instanceof ApiError && err.code === "authz.forbidden")
        setFormError(t("team.customRole.escalation"));
      else setFormError(stepMsg ?? errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Popup
      open={open}
      onClose={onClose}
      label={role ? t("team.customRole.editTitle") : t("team.customRole.title")}
      size="lg"
      closeOnBackdrop={false}
      data-testid="custom-role-dialog"
    >
      <div className="flex flex-col gap-3">
        <PopupHead
          icon="shield"
          title={role ? t("team.customRole.editTitle") : t("team.customRole.title")}
          onClose={onClose}
        />
        <p className="text-xs text-fg-secondary">
          {role ? t("team.customRole.editLead", { name: role.name }) : t("team.customRole.lead")}
        </p>
        {formError ? <Banner kind="error" title={formError} /> : null}
        {catalogue.isError ? <Banner kind="error" title={t("team.customRole.loadFailed")} /> : null}
        <Field label={t("team.customRole.nameLabel")} error={nameError} required>
          <Input
            value={name}
            maxLength={60}
            placeholder={t("team.customRole.namePlaceholder")}
            onChange={(e) => setName(e.target.value)}
            data-testid="custom-role-name"
          />
        </Field>
        <ul
          className="flex flex-col divide-y divide-subtle rounded-sm border border-card"
          aria-label={t("team.customRole.title")}
        >
          {areas.map((a) => {
            const opened = openAreas.has(a.key);
            const singles = [...a.view, ...a.manage, ...a.sensitive];
            return (
              <li key={a.key} className="flex flex-col gap-1.5 px-2.5 py-2" data-area={a.key}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="text-sm font-semibold">{areaText(a.key, "areas")}</span>
                    <span className="text-2xs text-fg-tertiary">{areaText(a.key, "areaHelp")}</span>
                  </span>
                  {a.view.length ? (
                    <Chip on={all(a.view)} onClick={() => toggleView(a)}>
                      <Icon name="eye" />
                      {t("team.customRole.view")}
                    </Chip>
                  ) : null}
                  {a.manage.length ? (
                    <Chip on={all(a.manage)} onClick={() => toggleManage(a)}>
                      <Icon name="pencil" />
                      {t("team.customRole.manage")}
                    </Chip>
                  ) : null}
                  <button
                    type="button"
                    aria-expanded={opened}
                    onClick={() =>
                      setOpenAreas((prev) => {
                        const next = new Set(prev);
                        if (opened) next.delete(a.key);
                        else next.add(a.key);
                        return next;
                      })
                    }
                    className="inline-flex min-h-7 items-center gap-1 rounded-xs px-1.5 text-xs font-medium text-link hover:bg-hover"
                  >
                    {opened ? t("team.customRole.hideAdvanced") : t("team.customRole.advanced")}
                    <Icon name={opened ? "chevronUp" : "chevronDown"} />
                  </button>
                </div>
                {opened ? (
                  <div className="grid gap-x-4 sm:grid-cols-2">
                    {singles.map((id) => (
                      <span key={id} className="flex items-center gap-1.5">
                        <Checkbox
                          label={permLabel(id)}
                          checked={picked.has(id)}
                          onChange={(e) => toggleOne(id, e.target.checked)}
                        />
                        {a.sensitive.includes(id) ? (
                          <Badge tone="warning" icon={null}>
                            {t("team.customRole.sensitive")}
                          </Badge>
                        ) : null}
                      </span>
                    ))}
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
        <p className="flex items-center gap-1 text-2xs text-fg-tertiary">
          <Icon name="shield" />
          {t("team.customRole.sensitiveHelp")}
        </p>
        <footer className="flex flex-wrap items-center gap-2 border-t border-subtle pt-2.5">
          <span className={cx("flex-1 text-xs", picked.size ? "text-fg" : "text-fg-tertiary")}>
            {t("team.customRole.chosen", { count: picked.size })}
          </span>
          <Button variant="secondary" onClick={onClose}>
            {t("team.customRole.cancel")}
          </Button>
          <Button
            onClick={() => void save()}
            loading={busy}
            loadingLabel={t("team.customRole.saving")}
            data-testid="custom-role-save"
          >
            {t("team.customRole.save")}
          </Button>
        </footer>
      </div>
    </Popup>
  );
}
